import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import YearSelector from './YearSelector';
import NotificationDropdown from './NotificationDropdown';
import api from '../services/api';
import {
  FiHome,
  FiUsers,
  FiBookOpen,
  FiUserCheck,
  FiDollarSign,
  FiFileText,
  FiMenu,
  FiX,
  FiLogOut,
  FiUser,
  FiSettings,
  FiChevronDown,
  FiBell
} from 'react-icons/fi';

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSection, setOpenSection] = useState('main'); // 'main', 'admin', 'settings', etc.
  const [openSubSection, setOpenSubSection] = useState(null); // 'profs', 'parents', 'classesMatieres', 'evaluations', null
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [etablissement, setEtablissement] = useState(null);
  const location = useLocation();
  const { logout, user, hasPermission } = useAuth();

  useEffect(() => {
    const fetchEtablissement = async () => {
      try {
        const res = await api.get('/etablissement');
        if (res.data.success && res.data.data) {
          setEtablissement(res.data.data);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'établissement:', error);
      }
    };
    fetchEtablissement();
  }, []);

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };
  const handleSubSection = (sub) => {
    setOpenSubSection(openSubSection === sub ? null : sub);
  };


  // Tableau de bord au premier niveau (hors section)
  const dashboardItem = { name: 'Tableau de bord', href: '/app/dashboard', icon: FiHome, permission: null };

  // Section VIE SCOLAIRE
  const vieScolaireNavigation = [
    { name: 'Années scolaires', href: '/app/annees-scolaires', icon: FiHome, permission: 'annees-scolaires.view' },
    { name: 'Élèves', href: '/app/eleves', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Parents', href: '/app/parents', icon: FiUsers, permission: 'eleves.view' },
    { name: 'Ouverture de classe', href: '/app/ouvertures-classes', icon: FiBookOpen, permission: 'ouvertures-classes.view' },
    { name: 'Inscriptions', href: '/app/inscriptions', icon: FiUserCheck, permission: 'inscriptions.view' },
    { name: 'Paiements', href: '/app/paiements', icon: FiDollarSign, permission: 'paiements.view' },
    { name: 'Evaluations', href: '/app/evaluations', icon: FiFileText, permission: 'evaluations.view' },
    { name: 'Absences', href: '/app/absences', icon: FiFileText, permission: 'absences.view' },
    { name: 'Incidents', href: '/app/incidents', icon: FiFileText, permission: 'incidents.view' },
    { name: 'Relevés de notes', href: '/app/releves-notes', icon: FiFileText, permission: 'releves-notes.view' },
  ];
  
  const adminNavigation = [
    { name: 'Utilisateurs', href: '/app/users', icon: FiSettings, permission: 'users.view' },
    { name: 'Rôles & Permissions', href: '/app/roles-permissions', icon: FiSettings, permission: 'roles.view' },
    { name: 'Notifications', href: '/app/notifications-admin', icon: FiBell, permission: 'notifications.admin' },
  ];
  
  const profsNavigation = [
    { name: 'Professeurs', href: '/app/professeurs', icon: FiUsers, permission: 'professeurs.view' },
    { name: 'Niveaux de qualification', href: '/app/niveau-qualif-prof', icon: FiFileText, permission: 'niveau-qualif-prof.view' },
    { name: 'Qualifications', href: '/app/qualifications', icon: FiFileText, permission: 'qualifications.view' },
    { name: 'Qualifications professeurs', href: '/app/prof-qualif', icon: FiFileText, permission: 'prof-qualif.view' },
  ];

  const parentsNavigation = [
    { name: 'Types de parenté', href: '/app/types-parentes', icon: FiSettings, permission: 'types-parentes.view' },
    { name: 'Religions', href: '/app/religions', icon: FiFileText, permission: 'religions.view' },
    { name: 'Professions', href: '/app/professions', icon: FiFileText, permission: 'professions.view' },
  ];

  const classesMatieresNavigation = [
    { name: 'Classes', href: '/app/classes', icon: FiBookOpen, permission: 'classes.view' },
    { name: 'Cycles', href: '/app/cycles', icon: FiBookOpen, permission: 'settings.view' },
    { name: 'Périodes', href: '/app/periodes', icon: FiFileText, permission: 'periodes.view' },
    { name: 'Branches', href: '/app/branches', icon: FiBookOpen, permission: 'branches.view' },
    { name: 'Matières', href: '/app/matieres', icon: FiBookOpen, permission: 'matieres.view' },
    { name: 'Classe-matières', href: '/app/classe-matieres', icon: FiBookOpen, permission: 'classe-matieres.view' },
  ];

  const evaluationsNavigation = [
    { name: 'Types d\'évaluations', href: '/app/type-evaluations', icon: FiFileText, permission: 'type-evaluations.view' },
  ];

  const settingsNavigation = [
    { name: 'Etablissement', href: '/app/etablissement', icon: FiSettings, permission: 'etablissement.view' },
  ];

  const isActive = (path) => location.pathname === path;

  // Filtrage du tableau de bord
  const showDashboard = !dashboardItem.permission || hasPermission(dashboardItem.permission);

  // Filtrage de la section VIE SCOLAIRE
  const filteredVieScolaireNavigation = vieScolaireNavigation.filter(item => {
    // Pour les enseignants, ne montrer que "Ouverture de classe"
    if (user?.role === 'enseignant') {
      return item.name === 'Ouverture de classe';
    }
    // Pour les autres rôles, utiliser les permissions
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus d'administration
  const filteredAdminNavigation = adminNavigation.filter(item => {
    // Les enseignants ne voient aucun menu d'administration
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de professeurs
  const filteredProfsNavigation = profsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de professeurs
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de parents
  const filteredParentsNavigation = parentsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de parents
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de classes et matières
  const filteredClassesMatieresNavigation = classesMatieresNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de classes et matières
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus d'évaluations
  const filteredEvaluationsNavigation = evaluationsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu d'évaluations
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  // Filtrage des menus de paramètres
  const filteredSettingsNavigation = settingsNavigation.filter(item => {
    // Les enseignants ne voient aucun menu de paramètres
    if (user?.role === 'enseignant') {
      return false;
    }
    return !item.permission || hasPermission(item.permission);
  });

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'block' : 'hidden'} md:block md:w-110 bg-gradient-to-b from-primary-500 via-primary-800 to-primary-700 shadow-2xl rounded-r-3xl border-r-2 border-primary-200 z-30`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b border-primary-600">
            <div className="flex items-center space-x-3">
              <div className="bg-white rounded-full p-2 shadow-md">
                <FiBookOpen className="h-7 w-7 text-primary-700" />
              </div>
              <span className="text-2xl font-extrabold tracking-wide text-white drop-shadow">Schola v3</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden"
            >
              <FiX className="h-6 w-6" />
            </button>
          </div>
          
          <nav className="flex-1 p-4">
            {/* Tableau de bord au premier niveau */}
            {showDashboard && (
              <div>
                <Link
                  to={dashboardItem.href}
                  className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                    isActive(dashboardItem.href)
                      ? 'bg-white/10 text-white font-bold'
                      : 'text-primary-100 hover:bg-white/10 hover:text-black'
                  }`}
                >
                  <dashboardItem.icon className="h-5 w-5" />
                  <span>{dashboardItem.name}</span>
                </Link>
              </div>
            )}

            {/* Section VIE SCOLAIRE */}
            {filteredVieScolaireNavigation.length > 0 && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 hover:text-black rounded transition mt-8"
                  onClick={() => toggleSection('vieScolaire')}
                >
                  <FiUsers className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Vie scolaire</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'vieScolaire' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'vieScolaire' && (
                  <ul className="space-y-2 mt-1">
                    {filteredVieScolaireNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors text-sm ${
                            isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                          }`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Administration section (déroulante) */}
            {filteredAdminNavigation.length > 0 && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 rounded transition mt-8 hover:text-black"
                  onClick={() => toggleSection('admin')}
                >
                  <FiSettings className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Administration</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'admin' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'admin' && (
                  <ul className="space-y-2 mt-1">
                    {filteredAdminNavigation.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                            isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                          }`}
                        >
                          <item.icon className="h-5 w-5" />
                          <span>{item.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            {/* Paramètres section (déroulante) */}
            {(filteredProfsNavigation.length > 0 || filteredParentsNavigation.length > 0 || filteredClassesMatieresNavigation.length > 0 || filteredEvaluationsNavigation.length > 0 || filteredSettingsNavigation.length > 0) && (
              <div>
                <button
                  className="flex items-center justify-start w-full px-3 py-2 text-lg font-bold text-blue-100 uppercase tracking-wider hover:bg-gray-100 rounded transition mt-8 hover:text-black"
                  onClick={() => toggleSection('settings')}
                >
                  <FiFileText className="h-5 w-5 mr-1" />
                  <span className="flex-1 text-left">Paramètres</span>
                  <FiChevronDown className={`transition-transform ${openSection === 'settings' ? 'rotate-180' : ''}`} />
                </button>
                {openSection === 'settings' && (
                  <>
                    {/* Sous-section PROFS */}
                    {filteredProfsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('profs')}
                      >
                        <FiUsers className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">PROFS</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'profs' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'profs' && filteredProfsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredProfsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                              isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section Parents */}
                    {filteredParentsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('parents')}
                      >
                        <FiUser className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">Parents</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'parents' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'parents' && filteredParentsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredParentsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                              isActive(item.href)
                              ? 'bg-white/10 text-white font-bold'
                              : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section CLASSES ET MATIERES */}
                    {filteredClassesMatieresNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold hover:text-black text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 ml-4"
                        onClick={() => handleSubSection('classesMatieres')}
                      >
                        <FiBookOpen className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">CLASSES ET MATIERES</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'classesMatieres' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'classesMatieres' && filteredClassesMatieresNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredClassesMatieresNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10 hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Sous-section Evaluations */}
                    {filteredEvaluationsNavigation.length > 0 && (
                      <button
                        className="flex items-center justify-start w-full px-3 py-2 text-base font-semibold text-primary-200 uppercase tracking-wider hover:bg-gray-100 rounded transition mb-2 hover:text-black ml-4"
                        onClick={() => handleSubSection('evaluations')}
                      >
                        <FiFileText className="h-5 w-5 mr-1" />
                        <span className="flex-1 text-left">Evaluations</span>
                        <FiChevronDown className={`transition-transform ${openSubSection === 'evaluations' ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                    {openSubSection === 'evaluations' && filteredEvaluationsNavigation.length > 0 && (
                      <ul className="space-y-2 ml-4">
                        {filteredEvaluationsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10  hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                    {/* Autres paramètres */}
                    {filteredSettingsNavigation.length > 0 && (
                      <ul className="space-y-2 mt-1">
                        {filteredSettingsNavigation.map((item) => (
                          <li key={item.name}>
                            <Link
                              to={item.href}
                              className={`flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                                isActive(item.href)
                                  ? 'bg-white/10 text-white font-bold'
                                  : 'text-primary-100 hover:bg-white/10  hover:text-black'
                              }`}
                            >
                              <item.icon className="h-5 w-5" />
                              <span>{item.name}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}
          </nav>

          {/* Le bouton de déconnexion a été déplacé dans le dropdown utilisateur du header */}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm relative z-20">
          <div className="flex items-center justify-between p-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden"
            >
              <FiMenu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center justify-between w-full">
              <YearSelector />
              
              {/* Logo et nom de l'établissement - centré */}
              <div className="flex-1 flex items-center justify-center">
                {etablissement && (
                  <div className="flex items-center space-x-3">
                    {etablissement.logo && (
                      <img 
                        src={`/uploads/${etablissement.logo}`}
                        alt="Logo établissement"
                        className="h-10 w-auto object-contain max-w-xs"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    )}
                    {etablissement.denomination && (
                      <span className="text-lg font-semibold text-gray-800 whitespace-nowrap">
                        {etablissement.denomination}
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-4">
                {/* Dropdown utilisateur */}
                <div className="relative">
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary-600 focus:outline-none"
                  >
                    <FiUser className="h-5 w-5" />
                    <span>{user?.nom} {user?.prenom}</span>
                    <span className="text-xs bg-gray-200 px-2 py-1 rounded">{user?.role}</span>
                    <FiChevronDown className={`h-4 w-4 transition-transform ${userDropdownOpen ? 'transform rotate-180' : ''}`} />
                  </button>
                  
                  {/* Menu dropdown */}
                  {userDropdownOpen && (
                    <>
                      {/* Overlay pour fermer le dropdown en cliquant à l'extérieur */}
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setUserDropdownOpen(false)}
                      ></div>
                      
                      {/* Menu dropdown */}
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                        <Link
                          to="/app/profile"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FiUser className="h-4 w-4" />
                          <span>Profil</span>
                        </Link>
                        <div className="border-t border-gray-200 my-1"></div>
                        <button
                          onClick={() => {
                            setUserDropdownOpen(false);
                            logout();
                          }}
                          className="flex items-center space-x-3 px-4 py-2 w-full text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        >
                          <FiLogOut className="h-4 w-4" />
                          <span>Déconnexion</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="h-6 w-px bg-gray-300"></div>
                <NotificationDropdown />
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;